import { useEffect } from "react";

import { TIMELINE_VIEW } from "../../../components/animation-timeline";
import { useAppSelector } from "../../../redux/hooks";
import { Sport } from "../../../redux/slices";
import { BasketballActionType, HockeyActionType, StencilType } from "../../../types";
import { PlayContextApi } from "./use-play-context-api";

const basketballActionTypeHotkeyMap = new Map<
  string,
  BasketballActionType.CUT | BasketballActionType.SCREEN | BasketballActionType.PASS | BasketballActionType.DRIBBLE
>([
  ["1", BasketballActionType.CUT],
  ["2", BasketballActionType.PASS],
  ["3", BasketballActionType.SCREEN],
  ["4", BasketballActionType.DRIBBLE],
]);

const hockeyActionTypeHotkeyMap = new Map<string, HockeyActionType>([
  ["1", HockeyActionType.SKATE],
  ["2", HockeyActionType.SKATE_BACKWARDS],
  ["3", HockeyActionType.PASS],
  ["4", HockeyActionType.SHOOT],
  ["5", HockeyActionType.SKATE_WITH_PUCK],
]);

export interface UsePlayDataKeyboardListenerProps {
  playContextApi: PlayContextApi;
}

export const usePlayDataKeyboardListener = ({ playContextApi }: UsePlayDataKeyboardListenerProps) => {
  const { selectItemApi, playStateApi, frameStencilsApi, animationTimerApi, drawToolbarApi } = playContextApi;
  /** ======== Remove On Delete ======== */
  const { selectedItems, clearItems } = selectItemApi;
  const { removeStencil, updateActionType } = frameStencilsApi;
  const { activeFrame } = playStateApi;
  const { isPlaying, playTimer, pauseTimer, timelineView, setTimelineView } = animationTimerApi;
  const { animationView } = drawToolbarApi;

  const sport = useAppSelector((state) => state.sport.name);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const key = event.key;
      const shiftKey = event.shiftKey;
      const controlOrMetaKey = event.ctrlKey || event.metaKey;

      const activeElement = document.activeElement;
      const isInputFocused = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement;
      const reactDraftWYSIWYGFocused = activeElement?.classList.contains("public-DraftEditor-content");
      // Only handle keydown events when an input or textarea is not focused.
      if (isInputFocused || reactDraftWYSIWYGFocused) {
        return;
      }

      if (["Backspace", "Delete"].includes(key)) {
        selectedItems.forEach((item) => removeStencil({ id: item }));
        clearItems();
      }

      if (animationView.isActive && key === " ") {
        // toggle play/pause
        if (isPlaying) {
          pauseTimer();
          return;
        } else {
          playTimer();
          return;
        }
      }

      if (key === "Escape") {
        drawToolbarApi.freeDraw.cancel();
        drawToolbarApi.fastPlace.cancel();
        return;
      }

      // undo/redo.  redo is ctrl+shift+z or ctrl+y
      if ((controlOrMetaKey && shiftKey && ["Z", "z"].includes(key)) || (controlOrMetaKey && key === "y")) {
        drawToolbarApi.redo();
        return;
      } else if (controlOrMetaKey && key === "z") {
        drawToolbarApi.undo();
        return;
      }

      if (key === "a") {
        drawToolbarApi.animationView.toggle();
        return;
      }

      if (key === "f" || key === "d") {
        drawToolbarApi.freeDraw.toggle();
        return;
      }

      if (key === "p") {
        drawToolbarApi.fastPlace.toggle();
        return;
      }

      if (key === "b") {
        drawToolbarApi.giveBall();
        return;
      }

      if (animationView.isActive && key === "v") {
        if (timelineView === TIMELINE_VIEW.FRAME) {
          setTimelineView(TIMELINE_VIEW.ALL);
        } else {
          setTimelineView(TIMELINE_VIEW.FRAME);
        }
        return;
      }

      // Numbers 1-5 Change line type of selectedItems to the matching index of the line type array.
      if (["1", "2", "3", "4", "5"].includes(key)) {
        selectedItems.forEach((item) => {
          const stencil = activeFrame?.frameStencils.find((stencil) => stencil.id === item);
          const layerType =
            sport === Sport.BASKETBALL
              ? basketballActionTypeHotkeyMap.get(key)
              : sport === Sport.HOCKEY
              ? hockeyActionTypeHotkeyMap.get(key)
              : undefined;
          if (stencil?.stencilType === StencilType.ACTION && layerType) {
            updateActionType({ id: stencil.id, layerType });
          }
        });
        return;
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    activeFrame?.frameStencils,
    animationView.isActive,
    clearItems,
    drawToolbarApi,
    isPlaying,
    pauseTimer,
    playTimer,
    removeStencil,
    selectedItems,
    setTimelineView,
    sport,
    timelineView,
    updateActionType,
  ]);
};
